import httpClient from '@/utils/http-client'

// Use fixed local metadata: server content must never select HTML render modes.
function textField (name, type = 'text') {
  return { name, type, editable: false, sortable: false }
}

function listMeta () {
  return {
    column: {
      email: textField('이메일'),
      phone: textField('연락처'),
      referrer: textField('문의한 CTA'),
      pathname: textField('문의한 페이지'),
      regDate: textField('접수일 (UTC)')
    },
    searchOption: {
      pathname: { name: '문의한 페이지', type: 'text' },
      regDate: { name: '접수기간 (UTC)', type: 'datetime|date|range' }
    }
  }
}

function detailMeta (info) {
  const field = {
    inquiryTitle: { name: '문의 정보', title: true },
    id: textField('접수번호'),
    email: textField('이메일'),
    phone: textField('연락처'),
    referrer: textField('문의한 CTA'),
    pathname: textField('문의한 페이지'),
    regDate: textField('접수일 (UTC)'),
    privacyAgreed: textField('개인정보 수집 동의'),
    privacyAgreedAt: textField('동의 확인 시각 (UTC)'),
    message: textField('문의 내용', 'text|longtext'),
    payload: textField('함께 보내는 내용', 'text|longtext')
  }
  const legacyFields = {
    company: textField('회사'),
    name: textField('이름'),
    selectionNow: textField('지금 쓰는 방식', 'text|longtext'),
    selectionNeed: textField('맞출 것', 'text|longtext'),
    selectionServer: textField('데이터를 둘 곳'),
    selectionPageViews: textField('월 페이지뷰')
  }
  for (const key of Object.keys(legacyFields)) {
    if (info[key] === undefined) continue
    if (!field.legacyTitle) field.legacyTitle = { name: '기존 양식 정보', title: true }
    field[key] = legacyFields[key]
  }
  return { field }
}

function display (value, fallback = '미입력') {
  return typeof value === 'string' && value.length ? value : fallback
}

function formatDate (value) {
  if (typeof value !== 'string' || !value) return '미입력'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '미입력' : date.toISOString().slice(0, 19).replace('T', ' ') + ' UTC'
}

function summary (row) {
  return {
    id: row.id,
    email: display(row.email),
    phone: display(row.phone),
    referrer: display(row.referrer, '기록 없음'),
    pathname: display(row.pathname),
    regDate: formatDate(row.regDate)
  }
}

function choices (value) {
  const values = Array.isArray(value) ? value.filter(item => typeof item === 'string' && item.length) : []
  return values.length ? values.join(', ') : '선택 없음'
}

function detail (row) {
  const info = {
    ...summary(row),
    message: display(row.message),
    payload: display(row.payload, '없음'),
    privacyAgreed: row.privacyAgreed === true ? '동의함' : '동의 기록 없음',
    privacyAgreedAt: formatDate(row.privacyAgreedAt)
  }
  // Old records remain readable without adding empty fields to the new form.
  for (const key of ['company', 'name']) {
    if (typeof row[key] === 'string' && row[key].length) info[key] = row[key]
  }
  const selection = row.selection || {}
  const selectionNow = choices(selection.now)
  const selectionNeed = choices(selection.need)
  const selectionServer = display(selection.srv, '선택 없음')
  const selectionPageViews = display(selection.pv, '선택 없음')
  if ([selectionNow, selectionNeed, selectionServer, selectionPageViews].some(value => value !== '선택 없음')) {
    Object.assign(info, { selectionNow, selectionNeed, selectionServer, selectionPageViews })
  }
  return { info, meta: detailMeta(info) }
}

function listPayload (params) {
  const page = params.pageNum === undefined ? 1 : params.pageNum
  const pageSize = params.pageSize === undefined ? 50 : params.pageSize
  if (!Number.isInteger(page) || page < 1 || page > 10000 || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) {
    throw new Error('페이지 설정을 확인해 주세요.')
  }
  const payload = { page, pageSize }
  const filters = params.searchFrom || {}
  if (filters.pathname !== undefined && filters.pathname !== null) {
    if (typeof filters.pathname !== 'string') throw new Error('문의한 페이지를 확인해 주세요.')
    if (filters.pathname.trim()) payload.pathname = filters.pathname.trim()
  }
  const range = filters.regDate || {}
  for (const [source, target] of [['start', 'startDate'], ['end', 'endDate']]) {
    const value = range[source]
    if (value === undefined || value === null || value === '') continue
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('접수기간을 확인해 주세요.')
    payload[target] = value
  }
  if (payload.startDate && payload.endDate && payload.startDate > payload.endDate) {
    throw new Error('접수 종료일을 시작일 이후로 선택해 주세요.')
  }
  return payload
}

// Call the existing client directly so personal data is not stored in the API cache.
export async function getInquiryList (params = {}) {
  const payload = listPayload(params)
  try {
    const response = await httpClient({ method: 'post', url: '/admin/inquiries/list', data: payload })
    const body = response.data
    if (!body || body.success !== true || !body.result || !Array.isArray(body.result.list)) throw new Error('Invalid response')
    return {
      success: true,
      result: {
        list: body.result.list.map(summary),
        count: body.result.count,
        meta: listMeta()
      }
    }
  } catch (error) {
    throw new Error('문의 목록을 불러오지 못했습니다. 잠시 뒤 다시 조회해 주세요.')
  }
}

export async function getInquiryDetail (params) {
  if (!params || typeof params.id !== 'string' || !/^[a-f\d]{24}$/i.test(params.id)) {
    throw new Error('문의 접수번호를 확인해 주세요.')
  }
  try {
    const response = await httpClient({ method: 'post', url: '/admin/inquiries/detail', data: { id: params.id } })
    const body = response.data
    if (!body || body.success !== true || !body.result || !body.result.info) throw new Error('Invalid response')
    return { success: true, result: detail(body.result.info) }
  } catch (error) {
    throw new Error('문의 상세를 불러오지 못했습니다. 목록에서 다시 선택해 주세요.')
  }
}
